import type { MoonFace, MoonLook, PuffinMood } from './halloween/hire-scene-frame';

export const SIGNIN_EMAIL_ID = 'signin-email';
export const SIGNIN_PASSWORD_ID = 'signin-password';

export type SigninFocus = 'email' | 'password' | null;
export type SigninValidationError = 'emailRequired' | 'emailInvalid' | 'passwordRequired';
export type SigninFieldErrors = { email?: string; password?: string };

export type SigninSceneState = {
  mood: PuffinMood;
  moonFace: MoonFace;
  moonLook: MoonLook;
  cheerAt: number;
};

/** How long the moon beams after a successful sign-in. */
export const JOY_DURATION_MS = 2600;
/** How long the moon stays startled after an error appears. */
export const SURPRISE_DURATION_MS = 1600;
/** How long the cheer plays before the app takes over. */
export const CHEER_BEFORE_REDIRECT_MS = 1200;

/** Pupil direction while a hidden password is typed: up and away from the form. */
export const MOON_LOOKS_AWAY: MoonLook = [0.5, -0.6];
/** Pupil direction otherwise: toward the form. */
export const MOON_LOOKS_AT_FORM: MoonLook = [-0.6, 0.35];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isValidEmail = (value: string) => {
  return EMAIL_PATTERN.test(value.trim());
};

export const validateSignin = ({
  email,
  password,
}: {
  email: string;
  password: string;
}): { email?: SigninValidationError; password?: SigninValidationError } => {
  if (!email.trim()) {
    return { email: 'emailRequired' };
  }
  if (!isValidEmail(email)) {
    return { email: 'emailInvalid' };
  }
  if (!password) {
    return { password: 'passwordRequired' };
  }
  return {};
};

/** Which sign-in field an element is, from its id. */
export const getSigninFocus = (elementId: string | undefined): SigninFocus => {
  if (elementId === SIGNIN_EMAIL_ID) {
    return 'email';
  }
  if (elementId === SIGNIN_PASSWORD_ID) {
    return 'password';
  }
  return null;
};

export const getPuffinMood = (focus: SigninFocus, isPasswordVisible: boolean): PuffinMood => {
  if (focus === 'password' && !isPasswordVisible) {
    return 'peek';
  }
  return focus ? 'look' : 'idle';
};

export const getMoonLook = (focus: SigninFocus, isPasswordVisible: boolean): MoonLook => {
  return focus === 'password' && !isPasswordVisible ? MOON_LOOKS_AWAY : MOON_LOOKS_AT_FORM;
};

type MoonClock = {
  now: number;
  joyAt: number;
  errorAt: number;
  hasError: boolean;
};

/** Priority: joy, then surprised, then worried, then awake, then asleep. */
export const getMoonFace = ({
  now,
  joyAt,
  errorAt,
  hasError,
  focus,
}: MoonClock & { focus: SigninFocus }): MoonFace => {
  if (joyAt && now - joyAt < JOY_DURATION_MS) {
    return 'joy';
  }
  if (hasError) {
    return now - errorAt < SURPRISE_DURATION_MS ? 'surprised' : 'worried';
  }
  return focus ? 'awake' : 'sleep';
};

/** Milliseconds until a timed moon face ends on its own, or null when none is running. */
export const getMoonFaceChangeIn = ({ now, joyAt, errorAt, hasError }: MoonClock) => {
  if (joyAt && now - joyAt < JOY_DURATION_MS) {
    return joyAt + JOY_DURATION_MS - now;
  }
  if (hasError && now - errorAt < SURPRISE_DURATION_MS) {
    return errorAt + SURPRISE_DURATION_MS - now;
  }
  return null;
};

/** Why the API rejected a sign-in (`LoginErrorCode` in api/admin/core/serializers/users.py). */
export type LoginErrorCode =
  | 'account_disabled'
  | 'account_locked'
  | 'forced_sso'
  | 'wrong_credentials';

export type LoginErrorBody = {
  nonFieldErrors?: string[];
  email?: string[];
  password?: string[];
  code?: LoginErrorCode;
};

/** The message the API sends when the company only allows SSO. */
const FORCED_SSO_MESSAGE = 'company_needs_sso';

/** The error body of a failed request, if it sent one. Network and server errors don't. */
export const getLoginErrorBody = (data: unknown): LoginErrorBody | undefined => {
  return typeof data === 'object' && data !== null ? (data as LoginErrorBody) : undefined;
};

export const isSsoRequired = (body: LoginErrorBody | undefined) => {
  return body?.code === 'forced_sso' || Boolean(body?.nonFieldErrors?.includes(FORCED_SSO_MESSAGE));
};

/** The field errors to show for a rejected sign-in, in plain language where we know the reason. */
export const getSigninErrors = (
  body: LoginErrorBody | undefined,
  messages: { wrongCredentials: string; unknownError: string },
): SigninFieldErrors => {
  if (body?.code === 'wrong_credentials') {
    return { password: messages.wrongCredentials };
  }
  if (body?.nonFieldErrors?.length) {
    return { password: body.nonFieldErrors.join(' ') };
  }
  if (body?.email || body?.password) {
    return { email: body.email?.join(' '), password: body.password?.join(' ') };
  }
  return { password: messages.unknownError };
};
